/**
 * Headless helpers: create a simulation, run a tape, collect events and
 * traces. These allocate freely; they are for tests, tools, and the app shell,
 * not for the tick.
 */

import { updateArms } from "./body";
import { probeGround } from "./collision";
import { drainEvents, EventBuffer, type SimEvent } from "./events";
import { hashStateHex, SimState } from "./state";
import { step } from "./step";
import { type Tape, TapeReader } from "./tape";
import { type CompiledTuning, compileTuning, defaultTuning, type Tuning } from "./tuning";
import { type CourseData, createInputFrame, type InputFrame, S } from "./types";
import { World } from "./world";

export class Sim {
  readonly world: World;
  tuning: CompiledTuning;
  /** Newest completed tick. */
  state: SimState;
  /** The tick before it, for render interpolation. */
  prev: SimState;
  readonly events = new EventBuffer();
  readonly input: InputFrame = createInputFrame();

  constructor(course: CourseData, tuning: Tuning = defaultTuning) {
    this.world = new World(course);
    this.tuning = compileTuning(tuning);
    this.state = new SimState();
    this.prev = new SimState();
    this.reset();
  }

  /** Start over at the course start, or at respawn point `respawn` when it is above 0. */
  reset(respawn = 0): void {
    const s = this.state;
    const fresh = new SimState();
    s.copyFrom(fresh);
    const w = this.world;
    const k = respawn > 0 && respawn < w.respawnCount ? respawn : -1;
    s.x = k >= 0 ? w.respawnX[k]! : w.startX;
    s.y = k >= 0 ? w.respawnY[k]! : w.startY;
    if (k >= 0) {
      s.respawnIndex = k;
      while (s.markIndex < w.markCount && w.markX[s.markIndex]! <= s.x) s.markIndex++;
    }
    s.leftRadius = 1;
    s.grounded = probeGround(this.world, s, this.tuning, 0.02) ? 1 : 0;
    if (s.grounded === 1) s.coyote = this.tuning.coyoteTicks;
    else s.state = S.Fall;
    s.ticksSinceLand = 1e9;
    updateArms(this.world, s, this.tuning);
    this.prev.copyFrom(s);
    this.events.clear();
  }

  setTuning(t: Tuning): void {
    this.tuning = compileTuning(t);
  }

  /** Advance one tick with `input` (defaults to the sim's own input frame). */
  step(input: InputFrame = this.input): void {
    const p = this.state;
    const n = this.prev;
    step(this.world, p, n, input, this.tuning, this.events);
    this.prev = p;
    this.state = n;
  }

  hash(): string {
    return hashStateHex(this.state);
  }
}

export interface RunResult {
  sim: Sim;
  events: SimEvent[];
  hash: string;
  ticks: number;
}

export interface RunHooks {
  /** Called after every tick with the sim (state is the new tick). */
  onTick?: (sim: Sim, input: InputFrame) => void;
}

/** Run a tape from a fresh sim and return the final state, events, and hash. */
export function runTape(
  course: CourseData,
  tape: Tape,
  tuning: Tuning = defaultTuning,
  hooks: RunHooks = {},
): RunResult {
  const sim = new Sim(course, tuning);
  if (tape.respawn) sim.reset(tape.respawn);
  const reader = new TapeReader(tape);
  const events: SimEvent[] = [];
  const input = createInputFrame();
  let ticks = 0;
  while (reader.next(input)) {
    sim.step(input);
    drainEvents(sim.events, events);
    hooks.onTick?.(sim, input);
    ticks++;
  }
  return { sim, events, hash: sim.hash(), ticks };
}
